import { cmfRedecSnapshotForPayload } from "./cmfRedecSnapshots";

const SOYIO_API_TOKEN = process.env.REACT_APP_SOYIO_API_TOKEN;
const SOYIO_API_BASE_URL =
  process.env.REACT_APP_SOYIO_API_BASE_URL ?? "https://sandbox.soyio.id";

if (!SOYIO_API_TOKEN) {
  console.warn(
    "[Canopy demo] REACT_APP_SOYIO_API_TOKEN is not configured."
  );
}

export type ApiResult<T = unknown> = {
  request?: {
    method: string;
    url: string;
    body?: unknown;
  };
  status: number;
  ok: boolean;
  response: T;
};

export type Entity = Record<string, unknown> & {
  id?: string;
  user_reference?: string;
  merged_into?: string;
  created_at?: string;
  updated_at?: string;
};

export type ConsentAction = Record<string, unknown> & {
  id?: string;
  created_at?: string;
  entity_id?: string;
  agreement_id?: string;
  evidence_id?: string;
  redec_pdf_hash?: string;
  redec_data?: {
    redec_id?: string;
    executive_nin?: string;
  };
};

export type Agreement = Record<string, unknown> & {
  id?: string;
  data_permissions?: Array<Record<string, unknown>>;
};

export type ConsentTemplate = Record<string, unknown> & {
  id?: string;
  name?: string;
  title?: string;
  description?: string;
};

export type CmfRedecDebtPayload = {
  codigo_interno: string;
  codigo_institucion: string;
  rut_consultado: string;
  medio_consentimiento: number;
  objetivo_consentimiento: number[];
  finalidad_consentimiento: number;
  fecha_inicio_otorgamiento: string;
  hora_inicio_otorgamiento: string;
  fecha_fin_consentimiento: string;
  hora_fin_consentimiento: string;
  hash: {
    algoritmo: "SHA-256";
    hash: string;
  };
};

export type RedecAccessPayload = {
  redec_id: string;
  access_date: string;
  access_time: string;
};

function authHeaders() {
  return {
    Authorization: `Bearer ${SOYIO_API_TOKEN}`,
    "Content-Type": "application/json",
  };
}

async function jsonResult<T>(
  response: Response,
  parseJson = true,
  request?: ApiResult["request"]
): Promise<ApiResult<T>> {
  const json = parseJson ? await response.json().catch(() => null) : null;
  return {
    request,
    status: response.status,
    ok: response.ok,
    response: json as T,
  };
}

export async function getEntitiesByUserReference(
  userReference: string
): Promise<ApiResult<{ entities?: Entity[] }>> {
  const where = encodeURIComponent(
    JSON.stringify({ user_reference: { "=": userReference } })
  );
  const response = await fetch(
    `${SOYIO_API_BASE_URL}/api/v1/entities?per_page=1&where=${where}`,
    { method: "GET", headers: authHeaders() }
  );
  return jsonResult(response, true, {
    method: "GET",
    url: `${SOYIO_API_BASE_URL}/api/v1/entities?per_page=1&where=${where}`,
  });
}

export async function createRedecConsentAction(params: {
  actionToken: string;
  entityNin: string;
  email?: string;
  userReference: string;
}): Promise<ApiResult<{ consent_action?: ConsentAction }>> {
  const body: Record<string, unknown> = {
    action_token: params.actionToken,
    entity_nin: params.entityNin,
    rut: params.entityNin,
    user_reference: params.userReference,
    ...(params.email ? { email: params.email } : {}),
  };

  const url = `${SOYIO_API_BASE_URL}/api/v1/redec/consent_actions`;
  const response = await fetch(
    url,
    {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(body),
    }
  );
  return jsonResult(response, true, { method: "POST", url, body });
}

export async function getRedecConsentAction(
  consentActionId: string
): Promise<ApiResult<{ consent_action?: ConsentAction }>> {
  const url = `${SOYIO_API_BASE_URL}/api/v1/redec/consent_actions/${consentActionId}`;
  const response = await fetch(
    url,
    { method: "GET", headers: authHeaders() }
  );
  return jsonResult(response, true, { method: "GET", url });
}

export async function getAgreement(
  agreementId: string
): Promise<ApiResult<{ agreement?: Agreement }>> {
  const url = `${SOYIO_API_BASE_URL}/api/v1/agreements/${agreementId}`;
  const response = await fetch(
    url,
    { method: "GET", headers: authHeaders() }
  );
  return jsonResult(response, true, { method: "GET", url });
}

export async function getRedecConsentTemplate(
  consentTemplateId: string
): Promise<ApiResult<{ consent_template?: ConsentTemplate }>> {
  const url = `${SOYIO_API_BASE_URL}/api/v1/redec/consent_templates/${consentTemplateId}`;
  const response = await fetch(
    url,
    { method: "GET", headers: authHeaders() }
  );
  return jsonResult(response, true, { method: "GET", url });
}

export async function downloadRedecReceiptPdf(
  consentActionId: string,
  options: { download?: boolean } = {}
): Promise<ApiResult<{ downloaded?: boolean; size_bytes?: number }>> {
  const url = `${SOYIO_API_BASE_URL}/api/v1/redec/consent_actions/${consentActionId}/download_receipt`;
  const response = await fetch(
    url,
    { method: "GET", headers: authHeaders() }
  );

  if (!response.ok) return jsonResult(response, true, { method: "GET", url });

  const blob = await response.blob();
  if (options.download) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `canopy-redec-receipt-${consentActionId}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  return {
    request: { method: "GET", url },
    status: response.status,
    ok: response.ok,
    response: { downloaded: true, size_bytes: blob.size },
  };
}

export async function getComplianceStatuses(
  entityId: string,
  consentTemplateId: string
): Promise<ApiResult<{ compliance_statuses?: Array<Record<string, unknown>> }>> {
  const where = encodeURIComponent(
    JSON.stringify({ consent_template_id: { "=": consentTemplateId } })
  );
  const url = `${SOYIO_API_BASE_URL}/api/v1/entities/${entityId}/agreement/compliance_statuses?where=${where}`;
  const response = await fetch(
    url,
    { method: "GET", headers: authHeaders() }
  );
  return jsonResult(response, true, { method: "GET", url });
}

export async function consultCmfRedecDebt(
  body: CmfRedecDebtPayload
): Promise<ApiResult<Record<string, unknown>>> {
  const snapshot = cmfRedecSnapshotForPayload(body);
  const snapshotResponse = snapshot?.response as
    | Record<string, unknown>
    | undefined;
  const status =
    typeof snapshotResponse?.code === "number" ? snapshotResponse.code : 404;
  const request = {
    method: "POST",
    url: "mock://cmf-redec/consulta",
    body,
  };

  if (!snapshot) {
    return {
      request,
      status: 404,
      ok: false,
      response: {
        error_code: "CMF_LOCAL_SNAPSHOT_NOT_FOUND",
        message:
          "No existe un snapshot CMF local para este RUT sandbox. Regenera los snapshots reales con npm run cmf:snapshots.",
      },
    };
  }

  const cmfNoRecords =
    status === 404 &&
    snapshotResponse?.status === false &&
    snapshotResponse.subcode === 404;

  return {
    request,
    status,
    ok: status >= 200 && status < 300 ? true : cmfNoRecords,
    response: snapshotResponse ?? {},
  };
}

export async function createRedecAccess(
  body: RedecAccessPayload
): Promise<ApiResult<{ accesses?: Array<Record<string, unknown>> }>> {
  const url = `${SOYIO_API_BASE_URL}/api/v1/redec/accesses`;
  const response = await fetch(url, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  return jsonResult(response, true, { method: "POST", url, body });
}
